import axios from "axios";
import { MKS_BACKEND_URL } from "src/utils/env";
import { GlobalUtils } from "src/utils/globalUtils";

const BASE_URL = MKS_BACKEND_URL;

// Backend enum values (com.mks.backend.enums.ConnectionStatus)
export type ConnectionStatus = "PENDING" | "ACCEPTED" | "DECLINED" | "HOLD" | "CANCELLED";

// Listing endpoint expects ConnectionStatus
export type ConnectionRequestStatus = ConnectionStatus;

// API statuses used by update endpoint
export type ConnectionUpdateStatus = "ACCEPTED" | "DECLINED" | "CANCELLED";

// Keep flexible until backend response shape is finalized.
export type ConnectionRequestDto = Record<string, any>;

export const ConnectionsService = {
  async sendConnectionRequest(senderProfileId: string | number, receiverProfileId: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.post(`${BASE_URL}/connections/send/request`, null, {
      params: { senderProfileId, receiverProfileId },
      headers: { Authorization: `Bearer ${token}` },
    });
    // Return full response so callers can validate HTTP status.
    return res;
  },

  async updateConnectionStatus(connectionId: string | number, status: ConnectionUpdateStatus) {
    const token = GlobalUtils.getToken();
    const res = await axios.put(`${BASE_URL}/connections/${encodeURIComponent(String(connectionId))}`, null, {
      params: { status },
      headers: { Authorization: `Bearer ${token}` },
    });
    // Return full response so callers can validate HTTP status.
    return res;
  },

  async getRequestsByMarriageProfileId(marriageProfileId: string | number, status: ConnectionRequestStatus) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(
      `${BASE_URL}/connections/requests/${encodeURIComponent(String(marriageProfileId))}`,
      {
        params: { status },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return res.data as ConnectionRequestDto[];
  },

  async getSentRequests(marriageProfileId: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(`${BASE_URL}/connections/profile-request/${encodeURIComponent(String(marriageProfileId))}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as ConnectionRequestDto[];
  },

  async getConnectionRequestById(marriageProfileId: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(`${BASE_URL}/connections/${encodeURIComponent(String(marriageProfileId))}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as ConnectionRequestDto;
  },

  /**
   * Fetch the number of connection requests sent today by a user.
   * GET /connections/daily-count/{email}
   */
  async getDailyCount(email: string): Promise<number> {
    const token = GlobalUtils.getToken();
    const res = await axios.get(
      `${BASE_URL}/connections/daily-count/${encodeURIComponent(email)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Backend returns { "dailyLimitCount": <number> }
    const data = res.data;
    if (data && typeof data === "object" && "dailyLimitCount" in data) {
      return Number(data.dailyLimitCount ?? 0);
    }
    // Fallback: plain number
    return typeof data === "number" ? data : Number(data ?? 0);
  },

};
